
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

import Page from '@root/common/base/Page';
import AdsLayout from '@root/common/components/layout/AdsLayout.vue';
import PodcastSkeletonLoader from '@root/common/components/base/loader/PodcastSkeletonLoader.vue';
import PodcastCategoryService from '@root/modules/podcast/services/podcastCategory.service';
import PodcastEpisodeService from '@root/modules/podcast/services/podcastEpisode.service';
import PodcastDetail from '@root/modules/podcast/components/PodcastDetail.vue';
import PodcastPaywall from '@root/modules/podcast/components/PodcastPaywall.vue';
import { PodcastCategory, PodcastAsArticle } from '@root/modules/podcast/types/podcast';
import Meta from '@root/common/utils/Meta';
import { stripTags } from '@root/common/filters/htmlFormater';
import getMediaData from '@root/modules/podcast/utils/getMediaData';
import { limit } from '@root/modules/podcast/config/podcast.config';
import getPodcastAdsSettings from '@root/modules/podcast/utils/getPodcastAdsSettings';
import isMobileView from '@root/common/utils/isMobileView';
import buildPodcastUrl from '@root/modules/podcast/utils/buildPodcastUrl';
import buildCategoryUrl from '../../category/utils/buildCategoryUrl';

dayjs.extend(duration);

interface Data {
  category: PodcastCategory | null;
  episode: PodcastAsArticle | null;
  isMobileView: boolean;
  loaded: boolean;
  limit: number;
  offset: number;
  shoudLoadMoreEpisodes: boolean;
  loadingEpisodes: boolean;
  key: number | null;
}

interface Computed {
  showPaywall: boolean;
  showPlayer: boolean;
}

interface Methods {
  loadMoreEpisodes: () => void;
}

export default Page.extend<Data, Methods, Computed, unknown>({
  components: {
    AdsLayout,
    PodcastSkeletonLoader,
    PodcastDetail,
    PodcastPaywall,
  },
  data() {
    return {
      category: null,
      episode: null,
      isMobileView: false,
      loaded: false,
      limit,
      offset: 0,
      shoudLoadMoreEpisodes: true,
      loadingEpisodes: false,
      key: null,
    };
  },
  computed: {
    showPaywall() {
      return this.$store.state.podcast.showPaywall;
    },
    showPlayer() {
      return Boolean(this.$player?.state.currentlyPlaying.audioId);
    },
  },
  methods: {
    async loadMoreEpisodes() {
      if (!this.category || this.loadingEpisodes) {
        return;
      }

      this.loadingEpisodes = true;
      const podcastEpisodeService = new PodcastEpisodeService();
      const categoryId = [this.category?.id];
      const orderDirection = this.category.parentCategory?.parentCategory?.id === 96603927 ? 'ASC' : 'DESC';
      const episodes = await podcastEpisodeService.fetch({ categoryId, offset: this.offset + this.limit, limit: this.limit, orderDirection });
      if (episodes?.length) {
        this.category.articles.items = [...this.category?.articles.items, ...episodes];
        this.key = this.category.articles.items.length;
        this.offset = this.offset + this.limit;
        this.loadingEpisodes = false;
      } else {
        this.shoudLoadMoreEpisodes = false;
      }
    },
  },
  beforeRouteLeave(to, from, next) {
    this.resetPageAdsSettings();

    next();
  },
  async fetch() {
    try {
      const { params, query } = this.$route;
      const variables = {
        parent: params.parent,
        slug: [params.category],
        page: query.page as string,
        getArticles: false,
        limit: this.limit,
      };

      const podcastCategoryService = new PodcastCategoryService();
      const category = await podcastCategoryService.fetch(variables);
      if (category) {
        const orderDirection = category.parentCategory?.parentCategory?.id === 96603927 ? 'ASC' : 'DESC';
        const categoryWithArticles = await podcastCategoryService.fetch({ ...variables, getArticles: true, orderDirection });
        if (categoryWithArticles) {
          // Get audio duration
          const mediaData = await getMediaData(categoryWithArticles.articles.items, null, false);
          if (mediaData?.length) {
            const categoryItemsWithAudioData = categoryWithArticles.articles.items.map((item) => {
              const mediaAsset = mediaData.find((mediaDataItem) => mediaDataItem.id === item.id);
              return { ...item, duration: mediaAsset?.assetDuration || null, video: mediaAsset?.video };
            });

            categoryWithArticles.articles.items = categoryItemsWithAudioData;
          }

          this.category = categoryWithArticles;
        }

        // Set current episode
        const id = Number(params.id);
        const podcastEpisodeService = new PodcastEpisodeService();
        const episodes = await podcastEpisodeService.fetch({ id: [id] });
        if (episodes?.length) {
          this.episode = episodes[0] as PodcastAsArticle;
        }

        if (this.episode) {
          // Set activeArticle & activeArticlePath for articleViewsStatistics plugin to execute
          this.$store.commit('article/setActiveArticle', this.episode);
          this.$store.commit('article/setActiveArticlePath', this.$route.fullPath);

          this.setPageAdsSettings(getPodcastAdsSettings({ $channelConfig: this.$channelConfig, $route: this.$route, article: this.episode }));
        }

        this.loaded = true;
      } else {
        // 404?
      }
    } catch (e) {
      this.handlePageError(e);
    }
  },
  mounted() {
    this.restoreScroll();
    this.isMobileView = isMobileView();
  },
  head() {
    if (this.episode) {
      const meta = new Meta(this.$channelConfig('meta').base, this.$route.path);

      meta.title = this.episode.seoFields?.seoTitle || stripTags(this.episode.content.title.html);
      meta.ogTitle = this.episode.seoFields?.ogTitle || stripTags(this.episode.content.title.html);
      meta.description = this.episode.seoFields?.seoDescription || this.episode.content.lead?.text || '';
      meta.ogDescription = this.episode.content.lead?.text || '';
      meta.type = 'article';

      const script = [...meta.script];
      const domain = 'www.delfi.lt';
      const fakeDomainEnabled = this.$config.FAKE_DOMAIN_ENABLED;

      const episodeWithMediaData = this.category?.articles.items.find((article) => article.id === this.episode?.id);

      // Inject ld+json into head
      const jsonLd: any = {
        '@context': 'https://schema.org',
        '@type': 'PodcastEpisode',
        name: this.episode.seoFields?.seoTitle || stripTags(this.episode.content.title.html),
        url: buildPodcastUrl({ article: this.episode, domain, fakeDomainEnabled }),
        datePublished: this.episode.publishAt,
        timeRequired: dayjs.duration(episodeWithMediaData?.duration || 0, 's').toISOString(),
        description: this.episode.seoFields?.seoDescription || this.episode.content.lead?.text || '',
        partOfSeries: {
          '@type': 'PodcastSeries',
          name: this.episode.primaryCategory.name,
          // @ts-ignore
          url: buildCategoryUrl({ category: this.episode.primaryCategory, domain, fakeDomainEnabled }),
        },
      };

      script.push({ hid: 'podcast-ld-json', type: 'application/ld+json', json: jsonLd });

      return {
        title: meta.title,
        meta: meta.data,
        style: meta.style,
        link: meta.link,
        script,
        __dangerouslyDisableSanitizers: ['style'],
      };
    }

    return {};
  },
});
